import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Stars, Html } from "@react-three/drei";
import * as THREE from "three";

/* Load a remote texture safely; returns null until loaded, null on failure */
function useSafeTexture(url) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    if (!url) return;
    let active = true;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      url,
      (t) => { t.colorSpace = THREE.SRGBColorSpace; if (active) setTex(t); },
      undefined,
      () => {}
    );
    return () => { active = false; };
  }, [url]);
  return tex;
}

/* ---------- One planet system (planet + its moons + rings + label) ---------- */
function PlanetSystem({ obj, moons, positions, angleRef, timeScale, selectedId, onSelect, onDeepDive, showLabels, showOrbits }) {
  const groupRef = useRef();
  const meshRef = useRef();
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tex = useSafeTexture(obj.texture);

  useFrame((_, delta) => {
    if (obj.orbit_radius > 0) {
      angleRef.current[obj.id] = (angleRef.current[obj.id] || Math.random() * Math.PI * 2) + obj.orbit_speed * delta * timeScale * 6;
      const a = angleRef.current[obj.id];
      groupRef.current.position.set(Math.cos(a) * obj.orbit_radius, 0, Math.sin(a) * obj.orbit_radius);
    }
    if (meshRef.current) meshRef.current.rotation.y += obj.rotation_speed * delta * timeScale * 2;
    groupRef.current.getWorldPosition(tmp);
    positions.current[obj.id] = tmp.clone();
  });

  const selected = selectedId === obj.id;

  return (
    <group ref={groupRef}>
      <mesh
        ref={meshRef}
        onClick={(e) => { e.stopPropagation(); onSelect(obj.id); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "auto"; }}
      >
        <sphereGeometry args={[obj.render_radius, 48, 48]} />
        {obj.emissive ? (
          <meshBasicMaterial key={tex ? "t" : "n"} color={tex ? "#ffffff" : obj.color} map={tex || null} />
        ) : (
          <meshStandardMaterial key={tex ? "t" : "n"} color={tex ? "#ffffff" : obj.color} map={tex || null}
            emissiveMap={tex || null} emissive={tex ? "#ffffff" : "#000000"} emissiveIntensity={0.14}
            roughness={0.85} metalness={0.1} />
        )}
      </mesh>

      {/* selection halo */}
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[obj.render_radius * 1.35, obj.render_radius * 1.5, 64]} />
          <meshBasicMaterial color="#00D8FF" side={THREE.DoubleSide} transparent opacity={0.9} />
        </mesh>
      )}

      {/* rings for gas giants */}
      {obj.has_rings && (
        <mesh rotation={[-Math.PI / 2.2, 0, 0]}>
          <ringGeometry args={[obj.render_radius * 1.5, obj.render_radius * 2.4, 96]} />
          <meshBasicMaterial color={obj.color} side={THREE.DoubleSide} transparent opacity={0.35} />
        </mesh>
      )}

      {showLabels && (
        <Html position={[0, obj.render_radius + 0.8, 0]} center distanceFactor={40} zIndexRange={[10, 0]}>
          <button
            data-testid={`label-${obj.id}`}
            onClick={(e) => { e.stopPropagation(); onSelect(obj.id); }}
            className="text-tel text-[9px] px-2 py-1 rounded-full whitespace-nowrap text-white/90 hover:text-cyan-300 transition-colors"
            style={{ background: "rgba(3,3,5,0.5)", border: "1px solid rgba(255,255,255,0.15)", backdropFilter: "blur(4px)" }}
          >
            {obj.name}
          </button>
        </Html>
      )}

      {/* moons orbit locally */}
      {moons.map((m) => (
        <Moon key={m.id} obj={m} positions={positions} angleRef={angleRef} timeScale={timeScale}
          selectedId={selectedId} onSelect={onSelect} onDeepDive={onDeepDive} showLabels={showLabels} parentPos={groupRef} />
      ))}
    </group>
  );
}

function Moon({ obj, positions, angleRef, timeScale, selectedId, onSelect, onDeepDive, showLabels }) {
  const groupRef = useRef();
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tex = useSafeTexture(obj.texture);
  useFrame((_, delta) => {
    angleRef.current[obj.id] = (angleRef.current[obj.id] || Math.random() * Math.PI * 2) + obj.orbit_speed * delta * timeScale * 6;
    const a = angleRef.current[obj.id];
    groupRef.current.position.set(Math.cos(a) * obj.orbit_radius, 0, Math.sin(a) * obj.orbit_radius);
    groupRef.current.getWorldPosition(tmp);
    positions.current[obj.id] = tmp.clone();
  });
  const selected = selectedId === obj.id;
  return (
    <group ref={groupRef}>
      <mesh
        onClick={(e) => { e.stopPropagation(); onSelect(obj.id); }}
        onDoubleClick={(e) => { e.stopPropagation(); onDeepDive(obj.id); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "auto"; }}
      >
        <sphereGeometry args={[obj.render_radius, 32, 32]} />
        <meshStandardMaterial key={tex ? "t" : "n"} color={tex ? "#ffffff" : obj.color} map={tex || null} roughness={0.9} />
      </mesh>
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[obj.render_radius * 1.5, obj.render_radius * 1.7, 48]} />
          <meshBasicMaterial color="#00D8FF" side={THREE.DoubleSide} />
        </mesh>
      )}
      {showLabels && (
        <Html position={[0, obj.render_radius + 0.35, 0]} center distanceFactor={30}>
          <span className="text-tel text-[8px] text-white/60 whitespace-nowrap">{obj.name}</span>
        </Html>
      )}
    </group>
  );
}

/* ---------- orbit ring lines ---------- */
function OrbitRing({ radius }) {
  const points = useMemo(() => {
    const p = [];
    for (let i = 0; i <= 128; i++) {
      const a = (i / 128) * Math.PI * 2;
      p.push(new THREE.Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius));
    }
    return p;
  }, [radius]);
  const geo = useMemo(() => new THREE.BufferGeometry().setFromPoints(points), [points]);
  return (
    <line geometry={geo}>
      <lineBasicMaterial color="#2a3550" transparent opacity={0.5} />
    </line>
  );
}

/* ---------- camera focus animation ---------- */
function CameraController({ focusId, focusToken, positions, radiusMap, controlsRef, closeUp }) {
  const { camera } = useThree();
  const anim = useRef({ active: false, t: 0, from: new THREE.Vector3(), fromTarget: new THREE.Vector3() });

  useEffect(() => {
    if (focusToken === 0) return;
    const a = anim.current;
    a.active = true;
    a.t = 0;
    a.from.copy(camera.position);
    a.fromTarget.copy(controlsRef.current ? controlsRef.current.target : new THREE.Vector3());
  }, [focusToken]); // eslint-disable-line

  useFrame((_, delta) => {
    const a = anim.current;
    if (!a.active) return;
    a.t = Math.min(1, a.t + delta / 1.7);
    const e = a.t < 0.5 ? 4 * a.t * a.t * a.t : 1 - Math.pow(-2 * a.t + 2, 3) / 2;

    const objPos = (focusId && positions.current[focusId]) ? positions.current[focusId] : new THREE.Vector3(0, 0, 0);
    const r = radiusMap[focusId] || 6;
    const dist = closeUp ? (r * 2.3 + 1.2) : (r * 5 + 9);
    const desiredCam = closeUp
      ? new THREE.Vector3(objPos.x + dist * 0.35, objPos.y + dist * 0.25, objPos.z + dist * 0.9)
      : new THREE.Vector3(objPos.x + dist * 0.5, objPos.y + dist * 0.45, objPos.z + dist * 0.8);

    camera.position.lerpVectors(a.from, desiredCam, e);
    if (controlsRef.current) {
      controlsRef.current.target.lerpVectors(a.fromTarget, objPos, e);
      controlsRef.current.update();
    }
    if (a.t >= 1) a.active = false;
  });
  return null;
}

/* ---------- scene ---------- */
function Scene({ objects, selectedId, focusToken, onSelect, onDeepDive, showLabels, showOrbits, paused, closeUp, controlsRef }) {
  const positions = useRef({});
  const angleRef = useRef({});
  const timeScale = paused ? 0 : 1;

  const planets = objects.filter((o) => o.parent === "sun");
  const sun = objects.find((o) => o.id === "sun");
  const moonsByParent = useMemo(() => {
    const m = {};
    objects.filter((o) => o.type === "moon").forEach((o) => {
      (m[o.parent] = m[o.parent] || []).push(o);
    });
    return m;
  }, [objects]);
  const radiusMap = useMemo(() => Object.fromEntries(objects.map((o) => [o.id, o.render_radius])), [objects]);

  return (
    <>
      <ambientLight intensity={0.38} />
      <pointLight position={[0, 0, 0]} intensity={4.5} distance={500} decay={0.7} color="#FFF6E0" />
      <Stars radius={300} depth={80} count={7000} factor={5} saturation={0} fade speed={0.6} />

      {sun && (
        <PlanetSystem obj={sun} moons={[]} positions={positions} angleRef={angleRef} timeScale={timeScale}
          selectedId={selectedId} onSelect={onSelect} onDeepDive={onDeepDive} showLabels={showLabels} showOrbits={showOrbits} />
      )}
      {showOrbits && planets.map((p) => <OrbitRing key={`orb-${p.id}`} radius={p.orbit_radius} />)}
      {planets.map((p) => (
        <PlanetSystem key={p.id} obj={p} moons={moonsByParent[p.id] || []} positions={positions}
          angleRef={angleRef} timeScale={timeScale} selectedId={selectedId} onSelect={onSelect} onDeepDive={onDeepDive}
          showLabels={showLabels} showOrbits={showOrbits} />
      ))}

      <CameraController focusId={selectedId} focusToken={focusToken} positions={positions}
        radiusMap={radiusMap} controlsRef={controlsRef} closeUp={closeUp} />
      <OrbitControls ref={controlsRef} enablePan enableDamping dampingFactor={0.08}
        minDistance={4} maxDistance={260} makeDefault />
    </>
  );
}

export default function SolarSystem({ objects, selectedId, focusToken, onSelect, onDeepDive, showLabels, showOrbits, paused, closeUp }) {
  const controlsRef = useRef();
  const solarObjects = objects.filter((o) => o.type !== "deep_space" && o.realm === "solar");
  return (
    <Canvas
      camera={{ position: [0, 45, 95], fov: 55, near: 0.1, far: 2000 }}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
      data-testid="solar-canvas"
    >
      <Scene objects={solarObjects} selectedId={selectedId} focusToken={focusToken} onSelect={onSelect} onDeepDive={onDeepDive}
        showLabels={showLabels} showOrbits={showOrbits} paused={paused} closeUp={closeUp} controlsRef={controlsRef} />
    </Canvas>
  );
}
